import { ANALYTICS_EVENTS } from '@nandosaus/constants';
import { cloneDeep, get, isEmpty, isUndefined, omitBy, trimStart } from 'lodash';
import { observer } from 'mobx-react';
import { PropTypes } from 'prop-types';
import { useRootStore } from '@nandosaus/state-management';
import gql from 'graphql-tag';
import React from 'react';
import styled from 'styled-components';
import { useMediaQuery } from 'react-responsive';

import { Alert } from '@components/alert';
import { analytics } from '@analytics';
import { Box } from '@components/grid';
import { ContentMatrix } from '@components/content-matrix';
import { Drawer } from '@components/drawer';
import { Feature } from '@components/feature';
import { fetch } from '@content/queries';
import { formatPerksShopProducts } from '@utils/perks-store-data';
import { InPageNav, NAV_ITEMS_COUNT } from '@components/in-page-nav';
import { Layout } from '@components/layout';
import { LoadingIndicator } from '@components/icons/loading-indicator';
import { logger } from '@utils/logger';
import { breakpoints, navigation } from '@constants';
import { PageHeaderBlock } from '@components/content-image';
import { PerksShopCart } from '@components/perks-shop-cart';
import { PerksShopItemCard } from '@components/card';
import { PerksShopProductDetailModal } from '@components/perks-shop-product-detail-modal';
import { perksShopProductsQuery } from '@content/query-snippets';
import { ProductCategoryList } from '@components/product-category-list';
import { setCacheHeaders } from '@utils/set-cache-headers';
import { SkeletonLoader } from '@components/skeleton-loader';
import { getFeatureFlags } from '@utils/feature-flags';

const BACKGROUND_IMAGE = [
  {
    url: '/static/images/peri-perks/peri-perks-page-header.jpg',
  },
];

const JOIN_PERKS_BUTTON_LINK = [
  {
    buttonText: 'Join PERi-Perks',
    link: [
      {
        typeHandle: 'internalLink',
        internalLink: [
          {
            uri: trimStart(navigation.CREATE_ACCOUNT, '/'),
          },
        ],
      },
    ],
  },
];

const PerksShopPage = ({ products, categorisedProducts, navSections, contentMatrix }) => {
  const { MemberStore, PerksShopStore } = useRootStore();
  const {
    addCartItem,
    clearSelectedItem,
    isVisibleCart,
    selectedItem,
    setIsVisibleCart,
    setSelectedItem,
  } = PerksShopStore;
  const { loading: isLoading, profile } = MemberStore;

  PerksShopStore.setProducts(products);

  const viewProduct = product => {
    setSelectedItem({ product: product.id });

    analytics.track(ANALYTICS_EVENTS.PRODUCT_CLICKED, {
      id: product.id,
      name: product.name,
      category: product.category ?? '',
    });
  };

  const handleCartOnClick = () => {
    setIsVisibleCart(true);
  };

  const handleSubmit = (selectedProductItem, selectedVariant, submittedQuantity) => {
    // save selectedProductItem clone for use in analytics below
    const selectedProductItemCopy = cloneDeep(selectedProductItem);
    const { product, totals } = selectedProductItemCopy;
    const { plu, name, productCategory } = product;

    addCartItem(selectedProductItem.product.id, selectedVariant, submittedQuantity);
    clearSelectedItem();

    analytics.track(ANALYTICS_EVENTS.PRODUCT_ADDED, {
      product_id: plu,
      name,
      price: totals.dollarValue,
      quantity: submittedQuantity,
      category: productCategory?.name,
    });
  };

  const pageHeadingProps = MemberStore.isSignedIn
    ? {
        heading: (
          <>
            {profile.givenName}, you have <PointsHighlight>{profile.balances.points} points</PointsHighlight> to burn!
          </>
        ),
      }
    : {
        heading: `Love Nando's? Join PERi-perks`,
        body: `Eat up and earn 1 point for every dollar you spend`,
        button: JOIN_PERKS_BUTTON_LINK,
      };

  const isViewportXl = useMediaQuery({ query: `(max-width: ${breakpoints.xl})` });
  const navItemsCount = isViewportXl ? NAV_ITEMS_COUNT.LG : NAV_ITEMS_COUNT.XL;

  return (
    <Layout
      title="Peri Perks"
      onScreenButtonProps={{
        itemCount: PerksShopStore.cartItemsCount,
        onClick: handleCartOnClick,
        price: `${PerksShopStore.totalCost} points`,
      }}
    >
      <Feature name="perks-shop">
        {isLoading ? (
          <>
            <LoadingIndicator />
            <SkeletonLoader height="2rem" />
          </>
        ) : (
          <>
            <PageHeaderBlock backgroundImage={BACKGROUND_IMAGE} contentPosition="center" {...pageHeadingProps} />
            {isEmpty(categorisedProducts) ? (
              <Alert type="info">
                Unfortunately there are no products available at this time. Please check back later.
              </Alert>
            ) : (
              <>
                <InPageNav
                  sections={navSections}
                  cartOnClick={handleCartOnClick}
                  quantity={PerksShopStore.cartItemsCount}
                  navItemsCount={navItemsCount}
                />
                <Box mb={4}>
                  <ContentMatrix blocks={contentMatrix} pageTitle="Peri Perks" />
                </Box>
                <ProductCategoryList
                  categories={categorisedProducts}
                  CardComponent={PerksShopItemCard}
                  viewProduct={viewProduct}
                />
              </>
            )}
          </>
        )}
        {!!selectedItem && (
          <PerksShopProductDetailModal
            handleSubmit={handleSubmit}
            isModalOpen={!!selectedItem.product}
            onClose={clearSelectedItem}
          />
        )}
        <Drawer open={isVisibleCart} anchor="right" onClose={() => setIsVisibleCart(false)}>
          <PerksShopCart onCloseCartClick={() => setIsVisibleCart(false)} />
        </Drawer>
      </Feature>
    </Layout>
  );
};

const PointsHighlight = styled.span`
  color: ${({ theme }) => theme.colors.periRed};
`;

PerksShopPage.getInitialProps = async ({ query, res }) => {
  const preview = get(query, 'x-craft-live-preview');
  const token = get(query, 'token');
  const slug = 'perks-shop';
  const featureFlags = await getFeatureFlags();

  const contentQuery = gql`
    query getContentFromCMS {
      content: entry(siteId: "${process.env.cms.siteId}", slug: "${slug}") {
        ... on pages_pages_Entry {
          ${ContentMatrix.query}
        }
      }
    }
  `;

  const productsQuery = gql`
    ${perksShopProductsQuery}
  `;

  try {
    const [data, content] = await Promise.all([
      fetch({
        query: productsQuery,
        headers: omitBy({ 'x-craft-live-preview': preview, token }, isUndefined),
      }),
      fetch({
        query: contentQuery,
        headers: omitBy({ 'x-craft-live-preview': preview, token }, isUndefined),
      }),
    ]);

    if (res) {
      if (isEmpty(get(data, 'perksShopProductData.perksShopProducts', []))) {
        return { statusCode: 404, featureFlags };
      }

      const isPreview = preview && token;
      setCacheHeaders({ res, isPreview });
    }

    const products = formatPerksShopProducts(get(data, 'perksShopProductData.perksShopProducts', []));
    const categories = get(data, 'perksShopProductData.categories', []);
    const navSections = categories.map(({ title }) => ({ id: title }));

    const categorisedProducts = categories.map(category => ({
      description: get(category, 'description'),
      handle: category.title,
      name: category.title,
      products: products.filter(entry => get(entry, 'category') === get(category, 'title')),
    }));

    const contentMatrix = get(content, 'content.contentMatrix', []);

    return { products, categorisedProducts, navSections, contentMatrix, featureFlags };
  } catch (err) {
    logger.error('CMS: Failed to fetch products', err);
    return { statusCode: 500, featureFlags };
  }
};

PerksShopPage.propTypes = {
  contentMatrix: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  categorisedProducts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  navSections: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default observer(PerksShopPage);
